/**
 * API Manager for handling network requests
 * 
 * This service automatically manages authentication tokens and provides
 * a unified interface for all backend API calls.
 * 
 * Usage:
 * ```typescript
 * import { apiManager } from './APIManager';
 * 
 * // All methods automatically include the stored auth token
 * const sessions = await apiManager.getChats();
 * const sessionData = await apiManager.generateSessionKey('verse');
 * ```
 */

import type { GetChatsResponseOptimized, GetSessionTurnsResponse } from '@/types/backend';
import type { UploadConversationRequest, UploadConversationResponse } from '@/types/backend';
import type { SubscriptionPlan, TokenPack, PaymentStatus, PaymentOrder, PaymentHistoryResponse, PaymentCheckResponse } from '@/types/payment';
import axios, { type AxiosInstance, AxiosError } from 'axios';

export class APIManager {
  private api: AxiosInstance;
  private tokenKey = 'translator_auth_token';

  constructor(baseUrl: string) {
    this.api = axios.create({
      baseURL: baseUrl + '/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 请求拦截器：添加认证token
    this.api.interceptors.request.use(config => {
      const token = this.getStoredToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // 响应拦截器：处理错误
    this.api.interceptors.response.use(
      response => response,
      (error: AxiosError) => {
        console.error(`API request error:`, error);
        if (error.response) {
          const errorText = error.response.data || error.response.statusText;
          throw new Error(`API request failed: ${error.response.status} ${errorText}`);
        }
        throw error;
      }
    );
  }
 
 /**
   * Generic request method with automatic token management
   */
  private async request<T>(
    endpoint: string,
    options: any = {}
  ): Promise<T> {
    try {
      const response = await this.api.request<T>({
        url: endpoint,
        ...options,
      });
      
      return response.data;
    } catch (error) {
      console.error(`API request error for ${endpoint}:`, error);
      throw error;
    }
  }

  private getStoredToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Generate a session key for Azure OpenAI service
   * @param voice Voice to use for the session
   * @param title Optional session title
   * @param trialId Optional trial ID for free trial users
   */
  async generateSessionKey(voice: string, title?: string, trialId?: string): Promise<any> {
    try {
      const headers: Record<string, string> = {};
      
      // 如果是免费试用用户，添加 X-Trial-Id header
      if (trialId) {
        headers['X-Trial-Id'] = trialId;
      }

      const result = await this.request<any>('/chat/sessions', {
        method: 'POST',
        headers,
        data: { 
          voice,
          title: title || `Conversation on ${new Date().toLocaleString('en-US')}`
        }
      });
      
      // Return the azure_session data for backward compatibility
      return {
        id: result.azure_session.id,
        client_secret: result.azure_session.client_secret,
        webrtc_url: result.azure_session.webrtc_url,
        deployment: result.azure_session.deployment,
        // Also include the chat session info
        chat_session: result.session
      };
    } catch (error) {
      console.error('Error creating chat session:', error);
      throw error;
    }
  }

  /**
   * Send WebRTC offer to the Azure OpenAI service
   * @param webrtcUrl WebRTC endpoint URL
   * @param deployment Model deployment name
   * @param ephemeralKey Session key for authentication
   * @param sdpOffer SDP offer from RTCPeerConnection
   */
  async sendWebRTCOffer(
    webrtcUrl: string,
    deployment: string,
    ephemeralKey: string,
    sdpOffer: string
  ): Promise<string> {
    try {
      const url = `${webrtcUrl}?model=${deployment}`;
      const response = await axios.post(url, sdpOffer, {
        headers: {
          'Authorization': `Bearer ${ephemeralKey}`,
          'Content-Type': 'application/sdp'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error sending WebRTC offer:', error);
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(`WebRTC offer rejected: ${error.response.data}`);
      }
      throw error;
    }
  }

  /**
   * Get chat sessions from backend (legacy method - loads all turns)
   * @param page Page number (default: 1)
   * @param limit Items per page (default: 20)
   * @param status Filter by status (optional)
   */
  // async getChats(page: number = 1, limit: number = 20, status?: 'active' | 'completed' | 'archived'): Promise<GetChatsResponse> {
  //   try {
  //     const params = new URLSearchParams({
  //       page: page.toString(),
  //       limit: limit.toString(),
  //       ...(status && { status })
  //     });

  //     return await this.request<GetChatsResponse>(`/chat/sessions?${params.toString()}`);
  //   } catch (error) {
  //     console.error('Error fetching chat sessions:', error);
  //     throw error;
  //   }
  // }

  /**
   * 🔑 新增：获取优化后的聊天会话列表（只包含最新2个turns）
   * @param page Page number (default: 1)
   * @param limit Items per page (default: 20)
   * @param status Filter by status (optional)
   */
  async getChatsOptimized(page: number = 1, limit: number = 5, status?: 'active' | 'completed' | 'archived'): Promise<GetChatsResponseOptimized> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(status && { status })
      });

      return await this.request<GetChatsResponseOptimized>(`/chat/sessions?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching optimized chat sessions:', error);
      throw error;
    }
  }

  /**
   * 🔑 新增：分页获取指定会话的所有turns
   * @param sessionId Session ID
   * @param page Page number (default: 1)
   * @param limit Items per page (default: 20, max: 100)
   */
  async getSessionTurns(sessionId: string, page: number = 1, limit: number = 5): Promise<GetSessionTurnsResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: Math.min(limit, 100).toString() // 限制最大值为100
      });

      return await this.request<GetSessionTurnsResponse>(`/chat/turns/${sessionId}?${params.toString()}`);
    } catch (error) {
      console.error(`Error fetching turns for session ${sessionId}:`, error);
      throw error;
    }
  }

  /**
   * Get a specific chat session
   * @param sessionId Session ID
   */
  async getChatSession(sessionId: string): Promise<any> {
    return await this.request<any>(`/chat/sessions/${sessionId}`);
  }

  /**
   * Upload conversation data to backend
   * @param sessionId Session ID
   * @param conversationTurns Array of conversation turns
   */
  async uploadConversation(request: UploadConversationRequest): Promise<UploadConversationResponse> {
    return await this.request<UploadConversationResponse>('/chat/conversations', {
      method: 'POST',
      data: request
    });
  }

  /**
   * Update session status
   * @param sessionId Session ID
   * @param status New status
   */
  async updateSessionStatus(sessionId: string, status: 'active' | 'completed' | 'archived'): Promise<any> {
    return await this.request<any>(`/chat/sessions/${sessionId}/status`, {
      method: 'PUT',
      data: { status }
    });
  }

  /**
   * Delete a chat session
   * @param sessionId Session ID
   */
  async deleteChatSession(sessionId: string): Promise<any> {
    return await this.request<any>(`/chat/sessions/${sessionId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Get user profile
   */
  async getUserProfile(): Promise<any> {
    return await this.request<any>('/user/profile');
  }

  /**
   * Update user profile
   * @param profileData Profile data to update
   */
  async updateUserProfile(profileData: any): Promise<any> {
    return await this.request<any>('/user/profile', {
      method: 'PUT',
      data: profileData
    });
  }

  /**
   * Get user token usage
   */
  async getUserTokens(): Promise<any> {
    return await this.request<any>('/user/tokens');
  }

  /**
   * Toggle MFA status
   * @param enabled Whether to enable MFA
   */
  async toggleMFA(enabled: boolean): Promise<any> {
    return await this.request<any>('/user/mfa', {
      method: 'PUT',
      data: { enabled }
    });
  }

  /**
   * Get available subscription plans
   */
  async getSubscriptionPlans(): Promise<{ data: SubscriptionPlan[] }> {
    return await this.request<{ data: SubscriptionPlan[] }>('/payment/plans');
  }

  /**
   * Get available token packs
   */
  async getTokenPacks(): Promise<{ data: TokenPack[] }> {
    return await this.request<{ data: TokenPack[] }>('/payment/token-packs');
  }

  /**
   * Get user's payment status including active subscriptions and token packs
   */
  async getPaymentStatus(): Promise<PaymentStatus> {
    return await this.request<PaymentStatus>('/payment/status');
  }

  /**
   * Create Alipay order for token pack purchase
   * @param packId Token pack ID to purchase
   */
  async createTokenPackAlipayOrder(packId: string): Promise<PaymentOrder> {
    return await this.request<PaymentOrder>('/payment/token/alipay', {
      method: 'POST',
      data: { packId }
    });
  }

  /**
   * Create Alipay order for subscription purchase
   * @param planId Subscription plan ID to purchase
   */
  async createSubscriptionAlipayOrder(planId: string): Promise<PaymentOrder> {
    return await this.request<PaymentOrder>('/payment/subscription/alipay', {
      method: 'POST',
      data: { planId }
    });
  }

  /**
   * Create Polar order for token pack purchase
   * @param packId Token pack ID to purchase
   */
  async createTokenPackPolarOrder(packId: string): Promise<PaymentOrder> {
    return await this.request<PaymentOrder>('/payment/token/polar', {
      method: 'POST',
      data: { packId }
    });
  }

  /**
   * Create Polar order for subscription purchase
   * @param planId Subscription plan ID to purchase
   */
  async createSubscriptionPolarOrder(planId: string): Promise<PaymentOrder> {
    return await this.request<PaymentOrder>('/payment/subscription/polar', {
      method: 'POST',
      data: { planId }
    });
  }

  /**
   * Get payment history
   * @param page Page number (default: 1)
   * @param limit Items per page (default: 10)
   */
  async getPaymentHistory(page: number = 1, limit: number = 10): Promise<PaymentHistoryResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    return await this.request<PaymentHistoryResponse>(`/payment/history?${params.toString()}`);
  }

  /**
   * Check Alipay payment status
   * @param orderId Order ID to check
   */
  async checkAlipayPayment(orderId: string): Promise<PaymentCheckResponse> {
    return await this.request<PaymentCheckResponse>(`/payment/check?orderId=${orderId}`);
  }

  /**
   * Check Polar payment status
   * @param orderId Order ID to check
   */
  async checkPolarPayment(orderId: string): Promise<PaymentCheckResponse> {
    return await this.request<PaymentCheckResponse>(`/payment/check/polar?orderId=${orderId}`);
  }

  /**
   * Add test tokens to user (testing environment only)
   */
  async addTestTokens(): Promise<any> {
    // 测试 debug 环境
    if (import.meta.env.DEV) {
      return await this.request<any>('/payment/test/add-tokens', {
        method: 'POST'
      });
    }
  }
}

// 导出单例实例
export const apiManager = new APIManager(import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'); 